/**
 * "Split folded aliases into separate revisions" (memo 5.7 / D9).
 *
 * For a calibre whose aliases are really its revisions (Seiko VK63 carrying
 * VK63A–VK63D), the curator ticks which to split out. Each becomes its own
 * calibre (a copy of this one's specs, to correct per revision), and all of them
 * join one revision line with this calibre — usually as its placeholder,
 * "revision unspecified". References do not move: that is the reassignment
 * tool's job, brand AKA by brand AKA.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { getCustomCalibreById, type CustomCalibreEntry } from '../../services/firebase';
import { saveLineage, splitFoldedAliases, type CalibreLineageEntry } from '../../services/calibreLineage';
import { foldedAliasCandidates, splitRevisionName } from '../../services/calibreLineageLogic';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  calibreId: string;
  manufacturerId: string;
  lineages: CalibreLineageEntry[];
  onSplit?: () => void;
}

export function SplitFoldedAliasesDialog({ isOpen, onClose, calibreId, manufacturerId, lineages, onSplit }: Props) {
  // Re-read on open: the calibre form may hold unsaved alias edits, and the
  // split works on what is stored.
  const [source, setSource] = useState<CustomCalibreEntry | null>(null);
  const [chosen, setChosen] = useState<Record<string, { on: boolean; name: string }>>({});
  const [lineageId, setLineageId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [placeholder, setPlaceholder] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    void getCustomCalibreById(calibreId).then((c) => {
      if (cancelled || !c) return;
      setSource(c);
      const cands = foldedAliasCandidates(c.name, c.alsoKnownAs ?? []);
      setChosen(Object.fromEntries(cands.map((k) => [k.alias, { on: true, name: splitRevisionName(c.name, k) }])));
      setLineageId(c.calibreLineageId ?? '');
      setNewLabel(c.name);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, calibreId]);

  const candidates = useMemo(
    () => (source ? foldedAliasCandidates(source.name, source.alsoKnownAs ?? []) : []),
    [source],
  );
  const picked = candidates.filter((c) => chosen[c.alias]?.on);

  const handleSplit = async () => {
    if (!source || picked.length === 0) return;
    setBusy(true);
    try {
      const id = lineageId || (await saveLineage({ baseLabel: newLabel, manufacturerId }));
      const created = await splitFoldedAliases({
        source,
        lineageId: id,
        splits: picked.map((c) => ({ alias: c.alias, name: chosen[c.alias].name.trim(), revisionLabel: c.revisionLabel })),
        markSourcePlaceholder: placeholder,
      });
      toast.success(`Created ${created.length} revision(s). Correct each one's specs, then mark the current one.`);
      onSplit?.();
      onClose();
    } catch (err) {
      console.error('Split failed', err);
      toast.error(err instanceof Error ? err.message : 'Split failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Split folded aliases into separate revisions" size="lg">
      {!source ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : candidates.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            None of {source.name}&rsquo;s aliases read as a revision of it (its name plus a letter, or a -N / .N
            mark).
          </p>
          <div className="flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Each ticked alias becomes its own calibre, copying {source.name}&rsquo;s specs (correct them per
            revision afterwards) and taking the alias with it. References stay where they are.
          </p>
          <ul className="space-y-2">
            {candidates.map((c) => (
              <li key={c.alias} className="flex flex-wrap items-center gap-2">
                <input
                  type="checkbox"
                  aria-label={`Split ${c.alias}`}
                  checked={chosen[c.alias]?.on ?? false}
                  onChange={(e) => setChosen({ ...chosen, [c.alias]: { ...chosen[c.alias], on: e.target.checked } })}
                />
                <span className="font-mono text-sm w-28">{c.alias}</span>
                <span className="text-xs text-gray-500">revision {c.revisionLabel} → new calibre</span>
                <input
                  value={chosen[c.alias]?.name ?? ''}
                  onChange={(e) => setChosen({ ...chosen, [c.alias]: { ...chosen[c.alias], name: e.target.value } })}
                  className="flex-1 min-w-[10rem] rounded border border-gray-300 px-2 py-1 text-sm"
                  aria-label={`New calibre name for ${c.alias}`}
                />
              </li>
            ))}
          </ul>
          <div className="space-y-2 border-t pt-3">
            <label className="block text-sm text-gray-700">
              Revision line{' '}
              <select
                value={lineageId}
                onChange={(e) => setLineageId(e.target.value)}
                className="ml-1 rounded border-gray-300 text-sm py-1"
              >
                <option value="">+ New line…</option>
                {lineages.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.baseLabel}
                  </option>
                ))}
              </select>
            </label>
            {!lineageId && (
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Seiko VK63"
                aria-label="New revision line label"
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            )}
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-0.5" checked={placeholder} onChange={(e) => setPlaceholder(e.target.checked)} />
              <span>
                Mark {source.name} as the line&rsquo;s placeholder (&ldquo;revision unspecified&rdquo;)
                <span className="block text-xs text-gray-500">
                  Right when the bare name is never a movement of its own. Leave off when it is a real first revision,
                  like the 1971 ETA 2824.
                </span>
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSplit()}
              loading={busy}
              disabled={picked.length === 0 || picked.some((c) => !chosen[c.alias].name.trim()) || (!lineageId && !newLabel.trim())}
            >
              Split {picked.length} alias(es)
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
